import React, { useEffect } from 'react'
import { CaretRightFilled } from '@ant-design/icons'
import { Button, Col, Collapse, Form, FormListFieldData, Row } from 'antd'
import { FormListProps } from 'antd/lib/form'

import { Typography } from 'src/storybook/components'
import { ButtonAdd, ButtonDelete } from 'src/storybook/components/Buttons'
import { ListNumber } from 'src/ui/ListNumber'

import { useFormFnUIContext } from './FormUI'

type TFieldChild = (
  field: FormListFieldData & {
    /** will be true in collapse version when this panel is opened */
    isActive?: boolean
  }
) => React.ReactNode

type TNumbering = 'numbered' | 'none'

export type TFormListProps = {
  renderTitle?: TFieldChild
  renderItem: TFieldChild
  numbering?: TNumbering
  title?: React.ReactNode
  addButtonProps: {
    title: React.ReactNode
    getItem: () => ANY
  }
  disabled?: boolean
  beforeItemRemove?: (field: FormListFieldData, remove: () => void) => void
  childWrapper?: ({
    children,
  }: {
    children: React.ReactNode
  }) => JSX.Element | null
  /** will delete the list values if it's unMounted.  */
  cleanOnUnMount?: boolean
} & Omit<FormListProps, 'children'>

const DefaultChildWrapper = ({ children }: { children: React.ReactNode }) => (
  <>{children}</>
)

const FormListUI = ({
  name,
  renderItem,
  numbering = 'numbered',
  renderTitle,
  title,
  addButtonProps,
  disabled: disabledFromProps,
  beforeItemRemove,
  childWrapper: ChildWrapper = DefaultChildWrapper,
  cleanOnUnMount,
  ...rest
}: TFormListProps) => {
  const form = Form.useFormInstance()
  const [activeKeys, setActiveKeys] = React.useState<React.Key[]>([])
  const { disabled: disabledFromContext } = useFormFnUIContext()

  const disabled = disabledFromProps || disabledFromContext

  const renderNumbering = (field: FormListFieldData) => {
    if (numbering === 'numbered') {
      return <ListNumber index={field.name + 1} />
    }
    return null
  }

  useEffect(() => {
    return () => {
      if (form && cleanOnUnMount) {
        form.setFieldValue(name, undefined)
      }
    }
  }, [form, name, cleanOnUnMount])

  return (
    <Col xs={24}>
      <ChildWrapper>
        <Row gutter={[0, 16]}>
          {title && (
            <Col xs={24}>
              <Typography variant="subHeader-16" weight="semi-bold">
                {title}
              </Typography>
            </Col>
          )}
          <Col xs={24}>
            <Form.List name={name} {...rest}>
              {(fields, { add, remove }, { errors }) => {
                const jsxErrors = !!errors?.length && (
                  <Col xs={24}>
                    <Form.Item>
                      <Form.ErrorList errors={errors} />
                    </Form.Item>
                  </Col>
                )
                if (renderTitle) {
                  // then it's a collapsible version
                  return (
                    <Row gutter={[0, 16]}>
                      <Col xs={24}>
                        <Collapse
                          ghost
                          expandIconPosition="end"
                          activeKey={activeKeys}
                          onChange={(keys) =>
                            setActiveKeys(Array.isArray(keys) ? keys : [keys])
                          }
                          expandIcon={() => null}
                          className="wh-antd-collapse no-horizontal-padding"
                          items={fields.map((field, index) => {
                            const fieldKey = field.name.toString()
                            return {
                              key: fieldKey,
                              forceRender: true,
                              label: (
                                <Row wrap={false} gutter={[16, 0]}>
                                  {numbering !== 'none' && (
                                    <Col>{renderNumbering(field)}</Col>
                                  )}
                                  <Col flex="none">
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        height: '100%',
                                      }}
                                    >
                                      {renderTitle({
                                        ...field,
                                        isActive: activeKeys.includes(fieldKey),
                                      })}
                                    </div>
                                  </Col>
                                  <Col flex="none">
                                    <Row>
                                      <Col flex="none">
                                        <Button
                                          ghost
                                          type="text"
                                          onClick={() => {
                                            if (activeKeys.includes(fieldKey)) {
                                              setActiveKeys(
                                                activeKeys.filter(
                                                  (key) => key !== fieldKey
                                                )
                                              )
                                            } else {
                                              setActiveKeys([
                                                ...activeKeys,
                                                fieldKey,
                                              ])
                                            }
                                          }}
                                          icon={
                                            <CaretRightFilled
                                              style={{
                                                transform: activeKeys.includes(
                                                  fieldKey
                                                )
                                                  ? 'rotate(90deg)'
                                                  : undefined,
                                                transition: 'transform 0.2s',
                                              }}
                                            />
                                          }
                                        />
                                      </Col>
                                      <Col flex="none">
                                        <ButtonDelete
                                          softDelete
                                          iconOnly
                                          onClick={() => {
                                            if (beforeItemRemove)
                                              beforeItemRemove?.(field, () =>
                                                remove(index)
                                              )
                                            else remove(index)
                                          }}
                                          disabled={disabled}
                                        />
                                      </Col>
                                    </Row>
                                  </Col>
                                </Row>
                              ),
                              children: (
                                <div style={{ paddingLeft: 32 }}>
                                  {renderItem({
                                    ...field,
                                    isActive: activeKeys.includes(fieldKey),
                                  })}
                                </div>
                              ),
                            }
                          })}
                        />
                      </Col>
                      <Col xs={24}>
                        <ButtonAdd
                          type="link"
                          onClick={() => {
                            add(addButtonProps.getItem())
                            setActiveKeys([
                              ...activeKeys,
                              fields.length.toString(),
                            ])
                          }}
                          disabled={disabled}
                        >
                          {addButtonProps.title}
                        </ButtonAdd>
                      </Col>
                      {jsxErrors}
                    </Row>
                  )
                }

                return (
                  <Row gutter={[0, 16]}>
                    {fields.map((field, index) => {
                      return (
                        <Col key={field.key} xs={24}>
                          <Row wrap={false} gutter={[16, 0]}>
                            {numbering !== 'none' && (
                              <Col>{renderNumbering(field)}</Col>
                            )}
                            <Col flex="auto">{renderItem(field)}</Col>
                            <Col flex="none">
                              <ButtonDelete
                                softDelete
                                iconOnly
                                onClick={() => {
                                  if (beforeItemRemove)
                                    beforeItemRemove?.(field, () =>
                                      remove(index)
                                    )
                                  else remove(index)
                                }}
                                disabled={disabled}
                              />
                            </Col>
                          </Row>
                        </Col>
                      )
                    })}
                    <Col xs={24}>
                      <ButtonAdd
                        type="link"
                        onClick={() => add(addButtonProps.getItem())}
                        disabled={disabled}
                      >
                        {addButtonProps.title}
                      </ButtonAdd>
                    </Col>
                    {jsxErrors}
                  </Row>
                )
              }}
            </Form.List>
          </Col>
        </Row>
      </ChildWrapper>
    </Col>
  )
}

export default FormListUI
